import * as React from 'react';
import { useMemo, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { useConfig } from '../../context';
import { CheckoutOrderFragment } from '@websky/graphql';
import { RefundState } from '../../Refund/types';
import Link from '../../Link';
import { capitalizeFirstLetter, getLocale } from '../../utils';
import { useRenderers } from '../../renderProps';
import FareConditionsModal from '../../FareConditions/components/Component';
import { SafeHtml } from '@shared/ui';

export interface Props {
	className?: string;
	order?: CheckoutOrderFragment | RefundState;
	onClickLink?: (e: HTMLSpanElement) => void;
	type: 'checkout' | 'exchange' | 'refund';
}

const InfoLinksLabel: React.FC<Props> = ({ className, order, type, onClickLink }) => {
	const { t, i18n } = useTranslation();
	const companyInfo = useConfig()?.global.companyInfo;
	const locale = getLocale();

	const [isFareModalOpen, setIsFareModalOpen] = useState(false);

	const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
		const target = e.target as HTMLSpanElement;

		if (target.hasAttribute('data-type')) {
			e.preventDefault();
			onClickLink?.(target);

			const linkType = target.getAttribute('data-type');
			if (linkType === 'termsOfTariff') {
				setIsFareModalOpen(true);
			}
		}
	};

	const hasPrivacyPolicy = companyInfo?.privacyPolicy;
	const hasInsurancePolicy = companyInfo?.insurance;

	const isCheckoutState = (state: CheckoutOrderFragment | RefundState): state is CheckoutOrderFragment =>
		(state as CheckoutOrderFragment)?.flight !== undefined;
	const isLocaleKA = locale === 'ka';

	const segmentId = isCheckoutState(order) ? order.flight.segments[0].segment.id : null;
	const flightId = isCheckoutState(order) ? order.flight.id : order?.flightId;
	const orderId = isCheckoutState(order) ? order.id : order?.orderId;

	const customAgreement = useMemo(() => {
		const ns = capitalizeFirstLetter(type);

		if (!i18n.exists('Agreement', { ns, fallbackLng: i18n.language })) {
			return null;
		}

		return t('Agreement', { ns });
	}, [type, i18n]);

	const { renderCheckoutLinksLabel, renderRefundLinksLabel } = useRenderers();

	return (
		<>
			<div className={cn(className)} onClick={onClick}>
				{customAgreement ? (
					<SafeHtml tag="span" html={customAgreement} />
				) : (
					<>
						{type === 'checkout' && (
							<>
								{renderCheckoutLinksLabel?.(companyInfo) ?? (
									<>
										<span>{t('Checkout:I accept the')} </span>
										<Link action={() => {}}>
											<span data-type="termsOfTariff">{t('Checkout:terms of tariff')}</span>
										</Link>
										{companyInfo?.legal ? (
											<>
												{hasPrivacyPolicy ? (
													isLocaleKA ? (
														<>&nbsp;{t('Checkout:and2')}</>
													) : (
														','
													)
												) : (
													<>&nbsp;{t('Checkout:and')}</>
												)}
												&nbsp;
												<Link target={'_blank'} action={companyInfo.legal}>
													{t('Checkout:terms of agreement')}
												</Link>
												{hasInsurancePolicy && (
													<span data-linkto="insurance">
														,&nbsp;
														<Link target="_blank" action={companyInfo.insurance}>
															{t('Checkout:terms of insurances')}
														</Link>
													</span>
												)}
												{hasPrivacyPolicy && (
													<>
														{isLocaleKA ? ', ' : <>&nbsp;</>}
														{t('Checkout:and')}
														&nbsp;
														<Link target={'_blank'} action={companyInfo.privacyPolicy}>
															{t('Checkout:processing of my personal data')}
														</Link>
													</>
												)}
											</>
										) : (
											<>
												&nbsp;
												{t('Checkout:and')}
												&nbsp;
												<Link target={'_blank'} action={companyInfo.privacyPolicy}>
													{t('Checkout:processing of my personal data')}
												</Link>
											</>
										)}
										<>
											{t('Checkout:infoLinksLabel_end', {
												defaultValue: '',
												fallbackLng: 'en'
											})}
										</>
									</>
								)}
							</>
						)}
						{type === 'exchange' && (
							<>
								{t('Exchange:I agree with the')}
								&nbsp;
								<Link action={() => {}}>
									<span data-type="termsOfTariff">{t('Exchange:terms of tariff')}</span>
								</Link>
								{companyInfo?.exareRulesURL && (
									<>
										{','}&nbsp;
										<Link target={'_blank'} action={companyInfo.exareRulesURL}>
											{t('Exchange:Terms of Service')}
										</Link>
									</>
								)}
								{companyInfo?.exarePrivacyPolicyURL && (
									<>
										&nbsp;{t('Exchange:And')}&nbsp;
										<Link target={'_blank'} action={companyInfo.exarePrivacyPolicyURL}>
											{t('Exchange:Privacy Policy')}
										</Link>
									</>
								)}
							</>
						)}
						{type === 'refund' && (
							<>
								{renderRefundLinksLabel?.(companyInfo) ?? (
									<>
										{t('Refund:I agree with the')}{' '}
										<Link action={() => {}}>
											<span data-type="termsOfTariff">{t('Refund:terms of tariff')}</span>
										</Link>
										{companyInfo?.exareRulesURL && (
											<>
												{','}&nbsp;
												<Link target={'_blank'} action={companyInfo.exareRulesURL}>
													{t('Refund:Terms of Service')}
												</Link>
											</>
										)}
										{companyInfo?.exarePrivacyPolicyURL && (
											<>
												{' '}
												{t('Refund:And')}&nbsp;
												<Link target={'_blank'} action={companyInfo.exarePrivacyPolicyURL}>
													{t('Refund:Privacy Policy')}
												</Link>
											</>
										)}
									</>
								)}
							</>
						)}
					</>
				)}
			</div>

			{order && (
				<FareConditionsModal
					segmentId={segmentId}
					flightId={flightId}
					orderId={orderId}
					isOpen={isFareModalOpen}
					onClose={() => setIsFareModalOpen(false)}
				/>
			)}
		</>
	);
};

export default InfoLinksLabel;
